import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LessonLayout from "./LessonLayout";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

// Quick simple projection — 30-yr retirement with fixed real returns of 7% stocks, 3% bonds, 60/40 blend
// After withdrawal, does the portfolio last 30/40/50 years?
const projectYears = (savings, annualWithdrawal, years, realReturn = 0.05) => {
  let b = savings;
  const path = [{ year: 0, balance: b }];
  for (let y = 1; y <= years; y++) {
    b = b * (1 + realReturn) - annualWithdrawal;
    path.push({ year: y, balance: Math.max(b, 0) });
    if (b <= 0) return { path, ruinedAt: y };
  }
  return { path, ruinedAt: null };
};

export default function LearnFourPercent() {
  const [savings, setSavings] = useState(1_000_000);
  const [withdrawalPct, setWithdrawalPct] = useState(4.0);
  const withdrawal = savings * (withdrawalPct / 100);

  const r30 = useMemo(() => projectYears(savings, withdrawal, 30), [savings, withdrawal]);
  const r40 = useMemo(() => projectYears(savings, withdrawal, 40), [savings, withdrawal]);
  const r60 = useMemo(() => projectYears(savings, withdrawal, 60), [savings, withdrawal]);

  const survived = (r) => r.ruinedAt === null;
  const survives30 = survived(r30);
  const survives40 = survived(r40);
  const survives60 = survived(r60);

  return (
    <LessonLayout
      meta={{
        number: 4,
        slug: "four-percent",
        time: "7 min read",
        title: "The 4% rule (and why it's wrong).",
        hook: "You've probably heard 'you can safely withdraw 4% of your savings every year in retirement.' It's the most quoted number in personal finance — and it's calibrated for a world that no longer exists.",
        seoDescription: "Interactive lesson on the 4% rule — where it came from, why it works for 30-year retirements, and why modern early retirees need to use 3.0-3.5% instead.",
        quickAnswer:
          "The 4% rule is safe for a traditional 30-year retirement, but it was calibrated on 1926–1976 data for exactly that horizon. For retirements of 40–60 years, a starting withdrawal rate of 3.0%–3.5% is the safer number, and flexible guardrail strategies beat any fixed rate.",
        bookCategory: "retirement",
      }}
      next={{ slug: "sequence-risk", title: "Sequence of returns — the silent killer" }}
    >
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
        <div className="flex flex-wrap gap-5 mb-6">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Retirement savings
            </Label>
            <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg w-fit">
              <span className="pl-3 text-[#5C6B62] text-sm">$</span>
              <Input
                type="number"
                value={savings}
                onChange={(e) => setSavings(Number(e.target.value) || 0)}
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-36"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Withdrawal rate
            </Label>
            <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg w-fit">
              <Input
                type="number"
                step={0.1}
                value={withdrawalPct}
                onChange={(e) => setWithdrawalPct(Number(e.target.value) || 0)}
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-20"
              />
              <span className="pr-3 text-[#5C6B62] text-sm">%</span>
            </div>
          </div>
          <div className="text-xs text-[#5C6B62] pb-2 max-w-xs">
            You&apos;d take out <strong>{fmt(withdrawal)}</strong> in year 1, then adjust for inflation each year.
            Assumes a 60/40 stock/bond blend earning ~5% real per year on average.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SurviveCard label="30-year retirement (age 65 → 95)" ruinedAt={r30.ruinedAt} survived={survives30} />
          <SurviveCard label="40-year retirement (age 55 → 95)" ruinedAt={r40.ruinedAt} survived={survives40} />
          <SurviveCard label="60-year retirement (age 35 → 95)" ruinedAt={r60.ruinedAt} survived={survives60} />
        </div>
      </div>

      <div className="prose prose-sm max-w-none text-[#1C2B23] space-y-4 leading-relaxed">
        <h2 className="text-2xl font-medium">Where the 4% rule came from</h2>
        <p>
          In 1994, a financial planner named William Bengen studied historical US stock returns from 1926
          to 1976. He asked: <em>&ldquo;What&apos;s the biggest annual withdrawal a retiree could take without
          running out of money for 30 years?&rdquo;</em>
        </p>
        <p>
          His answer: 4%. Even if you retired right before the 1929 crash, or in the middle of 1970s stagflation,
          a 4% withdrawal rate (adjusted for inflation each year) lasted 30 years.
        </p>

        <h2 className="text-2xl font-medium mt-8">Where the 4% rule breaks</h2>
        <p>
          It was calibrated for a <strong>30-year</strong> retirement. If you retire at 65 and live to 95 — great,
          the 4% rule works.
        </p>
        <p>
          But if you retire at 55, or 45, or 35 — you now need money to last 40, 50, or 60 years. The math
          changes dramatically. Compound interest turns from your friend to your enemy: any small overspend
          in early years compounds into ruin later.
        </p>
        <p>
          Play with the calculator above. Try:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>4% withdrawal, 30 years → survives</li>
          <li>4% withdrawal, 60 years → often runs out around year 45</li>
          <li>3.0% withdrawal, 60 years → survives even in bad markets</li>
        </ul>

        <h2 className="text-2xl font-medium mt-8">The right number for a long retirement</h2>
        <p>
          For a 50+ year retirement (typical if you FIRE early), the safe withdrawal rate is closer to
          <strong> 3.0% – 3.5%</strong>. On $1M savings, that&apos;s $30–35k/yr instead of $40k/yr.
        </p>
        <p>
          It sounds small. Compound over 50 years, it&apos;s the difference between a plan that survives and
          one that doesn&apos;t.
        </p>

        <h2 className="text-2xl font-medium mt-8">The takeaway</h2>
        <p className="text-[#2B5339] font-medium">
          Any &ldquo;rule of thumb&rdquo; you hear in personal finance was calibrated for someone else&apos;s life.
          Use the tool (like the main <a className="text-[#2B5339] underline" href="/">Centennial calculator</a>) to model YOUR numbers.
        </p>
      </div>
    </LessonLayout>
  );
}

function SurviveCard({ label, ruinedAt, survived }) {
  return (
    <div className={`rounded-xl p-5 border ${survived ? "bg-[#E8F0EB] border-[#2B5339]" : "bg-[#FDF2ED] border-[#F5C7B4]"}`}>
      <div className={`text-[10px] tracking-[0.2em] uppercase font-semibold mb-1 ${survived ? "text-[#2B5339]" : "text-[#C26747]"}`}>
        {label}
      </div>
      <div className={`font-mono-num text-2xl ${survived ? "text-[#2B5339]" : "text-[#C26747]"}`}>
        {survived ? "Survives" : `Runs out at year ${ruinedAt}`}
      </div>
    </div>
  );
}
